import { TellescopeModelFromName, useAuth, useTellescopeMutation } from "@tellescope/tanstack-query";
import { withKey } from "@tellescope/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { pendo } from "../../../../../tracking";
import {
  MessageCompositionFooter,
  MessageCompositionFooterPayload,
} from "../../../../../components/MessageCompositionFooter";
import { useDraftMutations } from "../../../hooks";

export interface InboxChatCompositionFooterProps {
  enduserId?: string;
  subject?: string;
  onSend?: (chatRoom: TellescopeModelFromName<"chat_rooms">, message: TellescopeModelFromName<"chats">) => void;
  onSaveDraftSuccess?: () => void;
  editing?: {
    message: TellescopeModelFromName<"chats">;
    isScheduled: boolean;
    onDone: () => void;
  };
}

export const InboxChatCompositionFooter = withKey(
  ({ enduserId, subject, onSend, onSaveDraftSuccess, editing }: InboxChatCompositionFooterProps) => {
    const { t } = useTranslation("inbox");
    const { currentUser } = useAuth();

    const createRoomMutation = useTellescopeMutation({
      model: "chat_rooms",
      method: "createOne",
      onError: (error) => console.error("Failed to create chat room:", error),
    });

    const sendMessageMutation = useTellescopeMutation({
      model: "chats",
      method: "createOne",
      onError: (error) => console.error("Failed to send chat message:", error),
    });

    const updateMessageMutation = useTellescopeMutation({
      model: "chats",
      method: "updateOne",
      onError: (error) => console.error("Failed to update chat message:", error),
    });

    const updateEditingMessage = (fields: { message: string; sendAt?: Date }) => {
      if (!editing) return;
      updateMessageMutation.mutate({ id: editing.message.id, ...fields }, { onSuccess: () => editing.onDone() });
    };

    const handleSend = ({ message, attachments }: MessageCompositionFooterPayload, sendAt?: Date) => {
      if (!enduserId || !subject || !currentUser.id) return;

      createRoomMutation.mutate(
        { title: subject, enduserIds: [enduserId] },
        {
          onSuccess: (createdRoom) => {
            sendMessageMutation.mutate(
              {
                roomId: createdRoom.id,
                message,
                userId: currentUser.id,
                attachments: attachments.map(({ type, name, secureName }) => ({
                  type,
                  name: name ?? "",
                  secureName,
                })),
                linkOpenTrackingIds: [],
                ...(sendAt && { sendAt }),
              },
              { onSuccess: (sentMessage) => onSend?.(createdRoom, sentMessage) }
            );
          },
        }
      );
    };

    const canSend =
      !!enduserId && !!subject && !!currentUser.id && !sendMessageMutation.isPending && !createRoomMutation.isPending;

    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const { saveDraft } = useDraftMutations();

    const handleSaveDraft = async ({ message, attachments }: MessageCompositionFooterPayload) => {
      if (!enduserId || !subject || !currentUser.id) return;
      setIsSavingDraft(true);
      const createdRoom = await createRoomMutation.mutateAsync({ title: subject, enduserIds: [enduserId] });
      if (!createdRoom?.id) {
        setIsSavingDraft(false);
        return;
      }

      await saveDraft({
        message,
        fields: {
          channel: "Chat",
          roomId: createdRoom.id,
          userId: currentUser.id,
          linkOpenTrackingIds: [],
          attachments: attachments.map(({ type, name, secureName }) => ({ type, name: name ?? "", secureName })),
        },
      });
      setIsSavingDraft(false);
      onSaveDraftSuccess?.();
    };

    if (editing) {
      const sendAt = editing.message.sendAt || undefined;

      return (
        <MessageCompositionFooter
          placeholder={t("Type your message...")}
          canSend={!updateMessageMutation.isPending}
          isUpdatePending={updateMessageMutation.isPending}
          enduserId={enduserId}
          channelType="chat"
          initialMessage={editing.message.message}
          initialAttachments={editing.message.attachments}
          isEditingScheduled={editing.isScheduled}
          initDate={sendAt}
          scheduleDialogKey={sendAt?.toISOString() ?? "no-init-date"}
          sendAtInPast={editing.isScheduled && !!sendAt && sendAt.getTime() <= Date.now()}
          submitOnEnter={!editing.isScheduled}
          onSubmit={({ message }) => updateEditingMessage({ message, sendAt: new Date() })}
          onSchedule={({ message }, newSendAt) => updateEditingMessage({ message, sendAt: newSendAt })}
          onSendNow={({ message }) => updateEditingMessage({ message, sendAt: new Date() })}
          onSaveScheduled={({ message }) => updateEditingMessage({ message })}
          onReschedule={({ message }, newSendAt) => updateEditingMessage({ message, sendAt: newSendAt })}
          trackingIds={{
            schedule: "chat-compose-schedule-message-dialog-open",
            reschedule: "chat-compose-reschedule-message-dialog-open",
          }}
        />
      );
    }

    return (
      <MessageCompositionFooter
        placeholder={t("Type your message...")}
        canSend={canSend}
        enduserId={enduserId}
        channelType="chat"
        onSubmit={(payload) => {
          pendo.track("new-chat-thread-send");
          handleSend(payload);
        }}
        onSchedule={(payload, sendAt) => handleSend(payload, sendAt)}
        onSaveDraft={enduserId && subject ? handleSaveDraft : undefined}
        isSavingDraft={isSavingDraft}
        trackingIds={{ schedule: "chat-compose-schedule-message-dialog-open" }}
      />
    );
  },
  ({ editing }) => editing?.message.id ?? "new"
);
